// =====================================================================
// src/modulos/troca_de_imobiliaria.js — o modal "Trocar imobiliária" (0102)
//
// Aberto pelo botão do quarto em Configurações (modulos/configuracoes.js).
// O desenho é o do esboço aprovado (/mnt/user-data/outputs/esboco_0102_troca
// .html, 02/11/2026). O MdM não tem tela de troca — lá só há uma
// imobiliária —, então o modal é montado com os componentes de lá: `.modal`
// (abrirModal), `.form-row`/`.label`/`.input`, `table.table-cards` (vira
// cartão no celular), `.badge-blue`, `.alert-warning`/`.alert-error` e os
// botões `.btn .btn-sm` (a escolha de cada reserva é um grupo de três; o
// escolhido em `.btn-primary`, o que a reserva não admite desabilitado).
//
// A regra é do banco: a lista e as opções de cada reserva vêm de
// previa_da_troca_de_imobiliaria; a troca é trocar_imobiliaria_do_quarto.
// O que a tela decide (o texto, a contagem, quando o botão habilita) está em
// lib/troca_de_imobiliaria.js, testado em node. O hotel não vê valor: a tela
// diz que o valor vai junto, sem mostrar quanto.
// =====================================================================
import { el, abrirModal, fecharModal, aviso } from '../ui.js';
import {
  OPCOES, imobiliariasParaTroca, rotuloDaReserva, periodoDaReserva, rotuloDaOpcao, notaDaReserva,
  estadoDaTroca, decisoesParaEnviar, avisoDaTroca, tituloDasReservas, valeAPartirDe, resumoDaTroca,
} from '../lib/troca_de_imobiliaria.js';

// quarto: { id, numero, imobiliaria_id, imobiliaria_nome }
// imobiliarias: as do hotel (com `ativo`); hoje: o dia do hotel (ISO);
// extra: { p_cliente } do Sistema; voltar(): reabre o quarto; depois(): recarrega
export async function abrirTrocaDeImobiliaria({ auth, quarto, imobiliarias, hoje, extra = {}, voltar, depois }) {
  let reservas;
  try { reservas = (await auth.rpc('previa_da_troca_de_imobiliaria', { p_unidade: quarto.id, ...extra })) || []; }
  catch (e) { aviso(e.message, 'erro'); return; }

  const antiga = quarto.imobiliaria_nome || 'imobiliária atual';
  const opcoesNova = imobiliariasParaTroca(imobiliarias, quarto.imobiliaria_id);
  const decisoes = {};
  let nova = null;
  const nomeNova = () => { const a = opcoesNova.find(x => x.id === nova); return a ? a.nome : null; };

  const erro = el('div', { id: 'troca-erro', class: 'alert alert-error hidden', role: 'alert' });
  const mostrarErro = msg => { erro.textContent = msg; erro.classList.remove('hidden'); };
  const seletor = el('select', { class: 'input', id: 'troca-nova', onchange: ev => { nova = ev.target.value || null; redesenhar(); } },
    el('option', { value: '' }, opcoesNova.length ? '— escolha a nova —' : '— nenhuma outra imobiliária ativa —'),
    opcoesNova.map(a => el('option', { value: a.id }, a.nome)));
  const corpo = el('tbody');
  const alerta = el('div', { class: 'alert alert-warning', id: 'troca-aviso', style: { fontSize: '12px', lineHeight: '1.5', margin: '10px 0' } });
  const confirmar = el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-troca-confirmar', style: { flex: '1' } }, 'Confirmar a troca');
  const pendencia = el('div', { id: 'troca-pendencia', class: 'usu-email', style: { fontSize: '12px', marginTop: '6px' } });

  function linha(r) {
    const d = decisoes[r.reserva_id];
    const botoes = OPCOES.map(op => {
      const pode = (r.opcoes || []).includes(op);
      return el('button', { type: 'button', class: 'btn btn-sm ' + (d === op ? 'btn-primary' : 'btn-secondary'), disabled: !pode,
        dataset: { opcao: op }, style: { fontSize: '11.5px', ...(pode ? {} : { textDecoration: 'line-through', opacity: '0.55' }) },
        onclick: () => { decisoes[r.reserva_id] = op; redesenhar(); } }, rotuloDaOpcao(op, antiga, nomeNova()));
    });
    const nota = notaDaReserva(r, d);
    return el('tr', { dataset: { reserva: r.reserva_id, decisao: d || '' } },
      el('td', { 'data-label': 'Reserva' },
        el('div', null, el('div', { style: { fontWeight: '600' } }, rotuloDaReserva(r)),
          // o titular, com nome e sobrenome, só para quem vê hóspedes (a prévia não manda aos outros)
          r.hospede ? el('div', { class: 'usu-email', style: { fontSize: '11px' } }, r.hospede) : null,
          r.hospede_dentro ? el('span', { class: 'badge badge-blue', style: { fontSize: '10.5px' } }, 'hóspede dentro') : null,
          r.atravessa ? el('div', { class: 'usu-email', style: { fontSize: '11px' } }, 'atravessa a data') : null)),
      el('td', { 'data-label': 'Período' }, periodoDaReserva(r)),
      el('td', { 'data-label': 'Destino' },
        el('div', null,
          el('div', { style: { display: 'flex', gap: '4px', flexWrap: 'wrap' } }, botoes),
          nota ? el('div', { class: 'troca-nota', style: { fontSize: '11px', marginTop: '4px', color: d === 'cancela' ? '#b45309' : '#6b7280' } }, nota) : null)));
  }
  function redesenhar() {
    corpo.replaceChildren(...reservas.map(linha));
    alerta.textContent = avisoDaTroca({ quarto: quarto.numero, antiga, nova: nomeNova() });
    const e = estadoDaTroca({ reservas, decisoes, nova });
    confirmar.disabled = !e.pronta;
    pendencia.textContent = e.pronta ? '' : e.erros.join(' · ');
  }

  const form = el('form', { id: 'form-troca', onsubmit: async ev => {
    ev.preventDefault();
    const e = estadoDaTroca({ reservas, decisoes, nova });
    if (!e.pronta) { mostrarErro(e.erros.join(' · ')); return; }
    confirmar.disabled = true;
    let resp;
    try {
      resp = await auth.rpc('trocar_imobiliaria_do_quarto', { p_unidade: quarto.id, p_de: quarto.imobiliaria_id, p_para: nova,
        p_decisoes: decisoesParaEnviar(reservas, decisoes), ...extra });
    } catch (e2) { mostrarErro(e2.message); confirmar.disabled = false; return; }
    fecharModal(); aviso(resumoDaTroca(resp)); if (depois) depois();
  } },
    el('h2', { class: 'modal-title', style: { marginBottom: '4px' } }, `Trocar a imobiliária do Quarto ${quarto.numero}`),
    el('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'De ', el('b', null, antiga), ' para:'),
    erro,
    el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'troca-nova' }, 'Nova imobiliária *'), seletor),
    el('div', { class: 'form-row' }, el('span', { class: 'label' }, 'Vale a partir de'),
      el('div', { class: 'input', id: 'troca-data', style: { background: '#f9fafb' } }, valeAPartirDe(hoje))),
    el('div', { id: 'troca-titulo-reservas', style: { fontSize: '13px', fontWeight: '700', marginTop: '16px' } }, tituloDasReservas(quarto.numero, reservas.length)),
    reservas.length
      ? [el('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Decida cada uma. A reserva que passa leva o valor e o lançamento.'),
         el('table', { class: 'table-cards', id: 'troca-reservas', style: { width: '100%', fontSize: '12.5px', marginTop: '8px' } },
           el('thead', null, el('tr', null, el('th', null, 'Reserva'), el('th', null, 'Período'), el('th', null, 'Destino'))), corpo)]
      : el('p', { class: 'vazio', id: 'troca-sem-reservas' }, 'Nenhuma reserva de hoje em diante — a troca é só do quarto.'),
    alerta,
    el('div', { style: { display: 'flex', gap: '8px', marginTop: '14px' } },
      el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-troca-cancelar', style: { flex: '1' }, onclick: () => { fecharModal(); if (voltar) voltar(); } }, 'Cancelar'),
      confirmar),
    pendencia);
  abrirModal(form, { largura: '640px' });
  redesenhar();
  seletor.focus();
}
